import { ICON_SIZES, IconSizes } from "@2u-landing-pages/types";

import { normalizeIconName } from "./icon-name-aliases";

type IconSelectionValue = {
  icon: string;
  size: IconSizes;
  color: string;
};

type IconSelectionField = "size" | "color";

const EMPTY_ICON_SELECTION: IconSelectionValue = {
  icon: "",
  size: "lg",
  color: "",
};

const normalizeIconSize = (value: unknown): IconSizes => {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  return ICON_SIZES.includes(normalizedValue as IconSizes) ? (normalizedValue as IconSizes) : "lg";
};

const normalizeIconColor = (value: unknown): string => {
  if (typeof value !== "string") return "";
  return value.trim();
};

export const parseIconSelection = (value: unknown): IconSelectionValue => {
  if (!value) return EMPTY_ICON_SELECTION;

  if (typeof value === "string") {
    try {
      return parseIconSelection(JSON.parse(value));
    } catch {
      return EMPTY_ICON_SELECTION;
    }
  }

  if (typeof value !== "object" || Array.isArray(value)) {
    return EMPTY_ICON_SELECTION;
  }

  const { icon, size, color } = value as Record<string, unknown>;

  return {
    icon: normalizeIconName(icon),
    size: normalizeIconSize(size),
    color: normalizeIconColor(color),
  };
};

export const getIconSelectionFromFields = (
  fields: Record<string, unknown> | undefined,
  requestedFields: IconSelectionField[] = []
) => {
  const parsedIconSelection = parseIconSelection(fields?.iconSelection);
  const legacyIcon = normalizeIconName(fields?.icon);
  const icon = parsedIconSelection.icon || legacyIcon;
  const size = normalizeIconSize(parsedIconSelection.size ?? fields?.icon_size);
  const color = normalizeIconColor(parsedIconSelection.color || fields?.icon_color);

  return {
    icon,
    ...(requestedFields.includes("size") ? { size } : {}),
    ...(requestedFields.includes("color") ? { color } : {}),
  } as {
    icon: string;
    size?: IconSizes;
    color?: string;
  };
};
